import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { projectsService } from '@/services/projects'
import { useAuth } from '@/contexts/AuthContext'
import type {
  Project,
  ProjectFilters,
  ProjectFormData,
  TaskFormData,
  MilestoneFormData,
  DocumentCategory,
} from '@/types/project'

// Projects list hook
export interface UseProjectsParams extends ProjectFilters {
  page?: number
  limit?: number
}

export function useProjects(params: UseProjectsParams = {}) {
  const { user } = useAuth()
  const { page = 1, limit = 20, ...filters } = params

  return useQuery({
    queryKey: ['projects', params],
    queryFn: () => projectsService.getAll(filters, page, limit),
    enabled: !!user,
  })
}

// Single project hook
export function useProject(id?: string) {
  const { user } = useAuth()

  return useQuery({
    queryKey: ['project', id],
    queryFn: () => projectsService.getById(id!),
    enabled: !!user && !!id,
  })
}

// Project stats hook
export function useProjectStats() {
  const { user } = useAuth()

  return useQuery({
    queryKey: ['projects', 'stats'],
    queryFn: () => projectsService.getStats(),
    enabled: !!user,
  })
}

// Create project mutation
export function useCreateProject() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: ProjectFormData) => projectsService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] })
    },
  })
}

// Update project mutation
export function useUpdateProject() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<ProjectFormData> }) =>
      projectsService.update(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['projects'] })
      queryClient.invalidateQueries({ queryKey: ['project', variables.id] })
    },
  })
}

// Update project status mutation
export function useUpdateProjectStatus() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: Project['status'] }) =>
      projectsService.updateStatus(id, status),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['projects'] })
      queryClient.invalidateQueries({ queryKey: ['project', variables.id] })
    },
  })
}

// Update project progression mutation
export function useUpdateProjectProgression() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, progression }: { id: string; progression: number }) =>
      projectsService.updateProgression(id, progression),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['projects'] })
      queryClient.invalidateQueries({ queryKey: ['project', variables.id] })
    },
  })
}

// Delete project mutation
export function useDeleteProject() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => projectsService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] })
    },
  })
}

// Tasks hooks
export function useProjectTasks(projectId?: string) {
  const { user } = useAuth()

  return useQuery({
    queryKey: ['project', projectId, 'tasks'],
    queryFn: () => projectsService.getTasks(projectId!),
    enabled: !!user && !!projectId,
  })
}

export function useCreateTask() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ projectId, data }: { projectId: string; data: TaskFormData }) =>
      projectsService.createTask(projectId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['project', variables.projectId, 'tasks'] })
      queryClient.invalidateQueries({ queryKey: ['project', variables.projectId] })
    },
  })
}

export function useUpdateTask() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ taskId, data }: { taskId: string; projectId: string; data: Partial<TaskFormData> }) =>
      projectsService.updateTask(taskId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['project', variables.projectId, 'tasks'] })
      queryClient.invalidateQueries({ queryKey: ['project', variables.projectId] })
    },
  })
}

export function useDeleteTask() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ taskId }: { taskId: string; projectId: string }) =>
      projectsService.deleteTask(taskId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['project', variables.projectId, 'tasks'] })
      queryClient.invalidateQueries({ queryKey: ['project', variables.projectId] })
    },
  })
}

// Additional task hooks
export function useOverdueTasks(projectId?: string) {
  const { user } = useAuth()

  return useQuery({
    queryKey: ['tasks', 'overdue', projectId],
    queryFn: () => projectsService.getOverdueTasks(projectId),
    enabled: !!user,
    refetchInterval: 60000, // Auto-refresh every 60 seconds
  })
}

export function useActiveTasks() {
  const { user } = useAuth()

  return useQuery({
    queryKey: ['tasks', 'active'],
    queryFn: () => projectsService.getActiveTasks(),
    enabled: !!user,
    refetchInterval: 60000,
  })
}

export function useMyTasks() {
  const { user } = useAuth()

  return useQuery({
    queryKey: ['tasks', 'my', user?.id],
    queryFn: () => projectsService.getMyTasks(user!.id),
    enabled: !!user,
    refetchInterval: 60000, // Auto-refresh every 60 seconds
  })
}

export function useReorderTask() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ taskId, newOrdre, projectId }: { taskId: string; newOrdre: number; projectId: string }) =>
      projectsService.reorderTask(taskId, newOrdre),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['project', variables.projectId, 'tasks'] })
    },
  })
}

// Documents hooks
export function useProjectDocuments(projectId?: string) {
  const { user } = useAuth()

  return useQuery({
    queryKey: ['project', projectId, 'documents'],
    queryFn: () => projectsService.getDocuments(projectId!),
    enabled: !!user && !!projectId,
  })
}

export function useUploadDocument() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      projectId,
      file,
      categorie,
      description,
      uploadedBy,
    }: {
      projectId: string
      file: File
      categorie: DocumentCategory
      description?: string
      uploadedBy?: string
    }) => projectsService.uploadDocument(projectId, file, categorie, description, uploadedBy),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['project', variables.projectId, 'documents'] })
      queryClient.invalidateQueries({ queryKey: ['project', variables.projectId] })
    },
  })
}

export function useDeleteDocument() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ documentId }: { documentId: string; projectId: string }) =>
      projectsService.deleteDocument(documentId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['project', variables.projectId, 'documents'] })
      queryClient.invalidateQueries({ queryKey: ['project', variables.projectId] })
    },
  })
}

export function useGenerateShareLink() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ documentId, expiresInHours }: { documentId: string; expiresInHours?: number }) =>
      projectsService.generateShareLink(documentId, expiresInHours),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project'] })
    },
  })
}

// Milestones hooks
export function useProjectMilestones(projectId?: string) {
  const { user } = useAuth()

  return useQuery({
    queryKey: ['project', projectId, 'milestones'],
    queryFn: () => projectsService.getMilestones(projectId!),
    enabled: !!user && !!projectId,
  })
}

export function useCreateMilestone() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ projectId, data }: { projectId: string; data: MilestoneFormData }) =>
      projectsService.createMilestone(projectId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['project', variables.projectId, 'milestones'] })
      queryClient.invalidateQueries({ queryKey: ['project', variables.projectId] })
    },
  })
}

export function useUpdateMilestone() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ milestoneId, data }: { milestoneId: string; projectId: string; data: Partial<MilestoneFormData> }) =>
      projectsService.updateMilestone(milestoneId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['project', variables.projectId, 'milestones'] })
      queryClient.invalidateQueries({ queryKey: ['project', variables.projectId] })
    },
  })
}

export function useCompleteMilestone() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ milestoneId }: { milestoneId: string; projectId: string }) =>
      projectsService.completeMilestone(milestoneId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['project', variables.projectId, 'milestones'] })
      queryClient.invalidateQueries({ queryKey: ['project', variables.projectId] })
    },
  })
}

export function useDeleteMilestone() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ milestoneId }: { milestoneId: string; projectId: string }) =>
      projectsService.deleteMilestone(milestoneId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['project', variables.projectId, 'milestones'] })
      queryClient.invalidateQueries({ queryKey: ['project', variables.projectId] })
    },
  })
}

// Activities hook
export function useProjectActivities(projectId?: string, limit = 50) {
  const { user } = useAuth()

  return useQuery({
    queryKey: ['project', projectId, 'activities'],
    queryFn: () => projectsService.getActivities(projectId!, limit),
    enabled: !!user && !!projectId,
  })
}
